import React, { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../services/firebase/config';
import { sendVerificationEmail } from '../services/firebase/auth';
import { useNavigate } from 'react-router-dom';

interface EmailVerificationCheckProps {
  onVerified?: () => void;
  redirectPath?: string;
}

const EmailVerificationCheck: React.FC<EmailVerificationCheckProps> = ({ 
  onVerified, 
  redirectPath = '/gyms' 
}) => {
  const [loading, setLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setLoading(false);
      
      if (user) {
        // Reload user to get latest verification status
        user.reload().then(() => {
          if (user.emailVerified) {
            setIsVerified(true);
            if (onVerified) {
              onVerified();
            }
          }
        }).catch((error) => {
          setError('Failed to check verification status: ' + error.message);
        });
      } else {
        // No user is signed in
        setError('You need to be signed in to verify your email.');
      }
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, [onVerified]);

  const handleResendVerification = async () => {
    try {
      setResendLoading(true);
      await sendVerificationEmail();
      alert('Verification email resent. Please check your inbox.');
    } catch (error: any) {
      setError('Failed to resend verification email: ' + error.message);
    } finally {
      setResendLoading(false);
    }
  };

  const handleProceedAnyway = () => {
    navigate(redirectPath);
  };

  if (loading) {
    return <div className="verification-loading">Checking verification status...</div>;
  }

  if (isVerified) {
    return (
      <div className="verification-success">
        <h3>Email Verified!</h3>
        <p>Your email has been successfully verified.</p>
        <button 
          onClick={() => navigate(redirectPath)} 
          className="auth-button"
        >
          Continue
        </button>
      </div>
    );
  }

  return (
    <div className="verification-message">
      <h3>Email Verification Required</h3>
      {error && <div className="auth-error">{error}</div>}
      <p>Please check your inbox and click the verification link to confirm your email address.</p>
      <p>If you don't see the email, check your spam folder.</p>
      <div className="verification-actions">
        <button 
          onClick={handleResendVerification} 
          className="auth-button secondary-button"
          disabled={resendLoading}
        >
          {resendLoading ? 'Sending...' : 'Resend Verification Email'}
        </button>
        <button 
          onClick={handleProceedAnyway} 
          className="auth-button"
        >
          Continue Anyway
        </button>
      </div>
    </div>
  );
};

export default EmailVerificationCheck;